import { useState } from "react";
import {
  BookOpenIcon,
  PlusIcon,
  Trash2Icon,
  ArrowUpIcon,
  ArrowDownIcon,
} from "lucide-react";

import {
  useRunbooks,
  useCreateRunbook,
  useUpdateRunbook,
  useDeleteRunbook,
  type Runbook,
  type RunbookContact,
} from "@/api/runbooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/store/auth";

// Known alert types (rule types + SInsight detection types). The input is
// a free-text datalist so admins can also author a runbook for a type not
// listed here.
const KNOWN_ALERT_TYPES = [
  "camera_offline",
  "camera_degraded",
  "motion_detected",
  "stream_error",
  "license_expiring",
  "site_unhealthy",
  "sinsight",
  "after_hours_intrusion",
  "zone_intrusion",
  "line_crossing",
  "crowd_threshold",
  "camera_tamper",
  "loitering",
  "abandoned_object",
  "fire_confirmed",
  "smoke",
  "flame",
];

interface EditorState {
  id: string | null;
  alert_type: string;
  title: string;
  steps: string[];
  contacts: RunbookContact[];
}

const EMPTY_EDITOR: EditorState = {
  id: null,
  alert_type: "",
  title: "",
  steps: [""],
  contacts: [],
};

function RunbookEditor({
  initial,
  onDone,
}: {
  initial: EditorState;
  onDone: () => void;
}) {
  const [state, setState] = useState<EditorState>(initial);
  const create = useCreateRunbook();
  const update = useUpdateRunbook(state.id ?? "");
  const saving = create.isPending || update.isPending;

  const setStep = (i: number, v: string) =>
    setState((s) => ({ ...s, steps: s.steps.map((x, j) => (j === i ? v : x)) }));
  const addStep = () => setState((s) => ({ ...s, steps: [...s.steps, ""] }));
  const removeStep = (i: number) =>
    setState((s) => ({ ...s, steps: s.steps.filter((_, j) => j !== i) }));
  const moveStep = (i: number, dir: -1 | 1) =>
    setState((s) => {
      const next = [...s.steps];
      const j = i + dir;
      if (j < 0 || j >= next.length) return s;
      [next[i], next[j]] = [next[j], next[i]];
      return { ...s, steps: next };
    });

  const setContact = (i: number, field: keyof RunbookContact, v: string) =>
    setState((s) => ({
      ...s,
      contacts: s.contacts.map((c, j) => (j === i ? { ...c, [field]: v } : c)),
    }));
  const addContact = () =>
    setState((s) => ({
      ...s,
      contacts: [...s.contacts, { name: "", phone: "", role: "" }],
    }));
  const removeContact = (i: number) =>
    setState((s) => ({ ...s, contacts: s.contacts.filter((_, j) => j !== i) }));

  const submit = async () => {
    const payload = {
      alert_type: state.alert_type.trim(),
      title: state.title.trim(),
      steps: state.steps.map((s) => s.trim()).filter(Boolean),
      contacts: state.contacts.filter((c) => c.name.trim()),
    };
    if (!payload.alert_type || !payload.title) return;
    if (state.id) await update.mutateAsync(payload);
    else await create.mutateAsync(payload);
    onDone();
  };

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        <h3 className="text-lg font-semibold">
          {state.id ? "Edit runbook" : "New runbook"}
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Alert type</Label>
            <Input
              list="known-alert-types"
              value={state.alert_type}
              onChange={(e) => setState((s) => ({ ...s, alert_type: e.target.value }))}
              placeholder="e.g. abandoned_object"
            />
            <datalist id="known-alert-types">
              {KNOWN_ALERT_TYPES.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </div>
          <div className="space-y-1">
            <Label>Title</Label>
            <Input
              value={state.title}
              onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
              placeholder="e.g. Abandoned bag response"
            />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-2">
          <Label>Steps</Label>
          {state.steps.map((step, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-5 text-right text-xs text-muted-foreground">
                {i + 1}.
              </span>
              <Input
                value={step}
                onChange={(e) => setStep(i, e.target.value)}
                placeholder="Step description"
              />
              <Button type="button" size="sm" variant="outline" onClick={() => moveStep(i, -1)}>
                <ArrowUpIcon className="h-3.5 w-3.5" />
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => moveStep(i, 1)}>
                <ArrowDownIcon className="h-3.5 w-3.5" />
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => removeStep(i)}>
                <Trash2Icon className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={addStep}>
            <PlusIcon className="mr-1 h-3.5 w-3.5" /> Add step
          </Button>
        </div>

        {/* Contacts */}
        <div className="space-y-2">
          <Label>Contacts</Label>
          {state.contacts.map((c, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <Input
                value={c.name}
                onChange={(e) => setContact(i, "name", e.target.value)}
                placeholder="Name"
              />
              <Input
                value={c.phone}
                onChange={(e) => setContact(i, "phone", e.target.value)}
                placeholder="Phone"
              />
              <Input
                value={c.role}
                onChange={(e) => setContact(i, "role", e.target.value)}
                placeholder="Role"
              />
              <Button type="button" size="sm" variant="outline" onClick={() => removeContact(i)}>
                <Trash2Icon className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={addContact}>
            <PlusIcon className="mr-1 h-3.5 w-3.5" /> Add contact
          </Button>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onDone}>
            Cancel
          </Button>
          <Button disabled={saving} onClick={submit}>
            {saving ? "Saving…" : "Save runbook"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function RunbookManager() {
  const role = useAuthStore((s) => s.user?.role);
  const { data } = useRunbooks();
  const del = useDeleteRunbook();
  const [editor, setEditor] = useState<EditorState | null>(null);

  if (role !== "admin") {
    return <p className="text-sm text-muted-foreground">Admin access required.</p>;
  }

  const openEdit = (rb: Runbook) =>
    setEditor({
      id: rb.id,
      alert_type: rb.alert_type,
      title: rb.title,
      steps: rb.steps.length ? rb.steps : [""],
      contacts: rb.contacts,
    });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <BookOpenIcon className="h-6 w-6" />
          Runbooks
        </h1>
        {!editor && (
          <Button onClick={() => setEditor({ ...EMPTY_EDITOR })}>
            <PlusIcon className="mr-2 h-4 w-4" /> New runbook
          </Button>
        )}
      </div>

      {editor && (
        <RunbookEditor initial={editor} onDone={() => setEditor(null)} />
      )}

      <div className="space-y-3">
        {(data?.items ?? []).map((rb) => (
          <Card key={rb.id}>
            <CardContent className="flex items-center justify-between p-4">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{rb.title}</p>
                  <Badge variant="online">{rb.alert_type}</Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {rb.steps.length} step{rb.steps.length === 1 ? "" : "s"} ·{" "}
                  {rb.contacts.length} contact{rb.contacts.length === 1 ? "" : "s"}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(rb)}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    if (!confirm(`Delete runbook "${rb.title}"?`)) return;
                    await del.mutateAsync(rb.id);
                  }}
                >
                  <Trash2Icon className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {data && data.items.length === 0 && !editor && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No runbooks yet. Create one to attach SOPs to alerts.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
